import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';

import { CountrySelect } from '@/components/shared/country-select';
import { CurrencySelect } from '@/components/shared/currency-select';
import {
  LocationPicker,
  type ChurchLocation,
} from '@/components/shared/location-picker';
import { PageHeader } from '@/components/shared/page-header';
import { Tip } from '@/components/shared/tip';
import { TimezoneSelect } from '@/components/shared/timezone-select';
import { Button } from '@/components/ui/button';
import { FormFieldError } from '@/components/ui/form-field-error';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { PhoneInput } from '@/components/ui/phone-input';
import { Stepper } from '@/components/ui/stepper';
import { toast } from '@/components/ui/toast';
import type { CountryCode } from '@/constants/countries';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { switchChurchAndOpen } from '@/lib/switch-church-workspace';
import {
  captureReferralCodeFromSearch,
  clearStoredReferralCode,
  getStoredReferralCode,
} from '@/lib/referral-link';
import { startFirstChurchSetup } from '@/lib/church-setup';
import {
  useChurchStore,
  type ChurchMembershipSummary,
} from '@/stores/church-store';

const LOCALES = [{ value: 'en', label: 'English' }];

const schema = z
  .object({
    name: z.string().trim().min(2, 'nameRequired'),
    campusName: z.string().trim().min(2, 'campusNameRequired'),
    contactEmail: z.string().trim().optional(),
    contactPhone: z.string().nullable().optional(),
    contactAlternatePhone: z.string().nullable().optional(),
    country: z.string().min(1, 'countryRequired'),
    timezone: z.string().min(1, 'timezoneRequired'),
    locationLatitude: z.number().nullable().optional(),
    locationLongitude: z.number().nullable().optional(),
    address: z.string().trim().max(500).nullable().optional(),
    currency: z.string().min(1, 'currencyRequired'),
    locale: z.string().min(1, 'localeRequired'),
  })
  .superRefine((data, ctx) => {
    const hasLat = data.locationLatitude != null;
    const hasLng = data.locationLongitude != null;
    if (hasLat !== hasLng) {
      ctx.addIssue({
        code: 'custom',
        message: 'locationIncomplete',
        path: hasLat ? ['locationLongitude'] : ['locationLatitude'],
      });
    }
  });

type Values = z.infer<typeof schema>;
type Step = 0 | 1 | 2 | 3;

const STEP_FIELDS: Record<Step, (keyof Values)[]> = {
  0: ['name', 'campusName'],
  1: [],
  2: ['country', 'timezone', 'locationLatitude', 'locationLongitude'],
  3: ['currency', 'locale'],
};

const LAST_STEP = 3 satisfies Step;

export const CreateChurchPage = () => {
  const { t } = useTranslation('churches');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectChurch = useChurchStore((state) => state.selectChurch);
  const [step, setStep] = useState<Step>(0);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    captureReferralCodeFromSearch(searchParams);
  }, [searchParams]);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      campusName: t('fields.campusNameDefault'),
      contactEmail: '',
      contactPhone: null,
      contactAlternatePhone: null,
      country: 'GH',
      timezone: 'Africa/Accra',
      locationLatitude: null,
      locationLongitude: null,
      address: null,
      currency: 'GHS',
      locale: 'en',
    },
  });

  const country = form.watch('country');
  const locationLatitude = form.watch('locationLatitude');
  const locationLongitude = form.watch('locationLongitude');
  const address = form.watch('address');

  const mapLocation = useMemo((): ChurchLocation | null => {
    if (locationLatitude != null && locationLongitude != null) {
      return {
        latitude: locationLatitude,
        longitude: locationLongitude,
        address,
      };
    }
    return null;
  }, [address, locationLatitude, locationLongitude]);

  const create = useMutation({
    mutationFn: (values: Values) => {
      const referralCode = getStoredReferralCode();
      return api.post<ChurchMembershipSummary>(
        '/churches',
        {
          name: values.name,
          campusName: values.campusName,
          country: values.country,
          defaultTimezone: values.timezone,
          baseCurrency: values.currency,
          defaultLocale: values.locale,
          ...(values.contactEmail?.trim()
            ? { contactEmail: values.contactEmail.trim() }
            : {}),
          ...(values.contactPhone ? { contactPhone: values.contactPhone } : {}),
          ...(values.contactAlternatePhone
            ? { contactAlternatePhone: values.contactAlternatePhone }
            : {}),
          ...(values.locationLatitude != null && values.locationLongitude != null
            ? {
                locationLatitude: values.locationLatitude,
                locationLongitude: values.locationLongitude,
                address: values.address?.trim() || null,
              }
            : {}),
          ...(referralCode ? { referralCode } : {}),
        },
        { withoutChurch: true },
      );
    },
    onSuccess: async (result) => {
      clearStoredReferralCode();
      startFirstChurchSetup(result.church.id);
      toast.success(t('create.success'));
      useChurchStore.setState((state) => ({
        memberships: [
          ...state.memberships.filter((row) => row.church.id !== result.church.id),
          result,
        ],
      }));
      await switchChurchAndOpen({
        action: () => selectChurch(result.church.id),
        navigate,
        churchName: result.church.name,
      });
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setSubmitError(tErrors(error.code, error.params));
        return;
      }
      setSubmitError(tErrors('UNEXPECTED'));
    },
  });

  const steps = [
    { id: 'church', label: t('create.steps.church') },
    { id: 'contact', label: t('create.steps.contact') },
    { id: 'location', label: t('create.steps.location') },
    { id: 'preferences', label: t('create.steps.preferences') },
  ];

  const stepDescriptions = [
    t('create.stepDescription.church'),
    t('create.stepDescription.contact'),
    t('create.stepDescription.location'),
    t('create.stepDescription.preferences'),
  ] as const;

  const fieldError = (key: keyof Values) => {
    const code = form.formState.errors[key]?.message;
    if (!code) return undefined;
    if (key === 'locationLatitude' || key === 'locationLongitude') {
      return code === 'locationIncomplete' ? t('create.validation.locationIncomplete') : undefined;
    }
    return t(`create.validation.${code}`);
  };

  const goNext = async () => {
    const valid = await form.trigger(STEP_FIELDS[step]);
    if (!valid) return;
    setStep((current) => (current < LAST_STEP ? ((current + 1) as Step) : current));
  };

  const goBack = () => {
    setStep((current) => (current > 0 ? ((current - 1) as Step) : current));
  };

  const submit = form.handleSubmit((values) => {
    setSubmitError(null);
    create.mutate(values);
  });

  return (
    <div className="mx-auto w-full max-w-xl px-4 py-10">
      <PageHeader
        title={t('create.title')}
        description={stepDescriptions[step]}
        headerActions={
          <Button type="button" variant="link" asChild>
            <Link to="/churches/select">{t('create.setupLater')}</Link>
          </Button>
        }
      />

      <form
        className="flex flex-col gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          if (step < LAST_STEP) {
            void goNext();
            return;
          }
          void submit();
        }}
        noValidate
      >
        <Stepper steps={steps} current={step} label={t('create.stepperLabel')} />

        {step === 0 ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="church-name">{t('fields.name')}</Label>
              <Input
                id="church-name"
                {...form.register('name')}
                placeholder={t('fields.namePlaceholder')}
                autoFocus
              />
              <FormFieldError message={fieldError('name')} />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="campus-name">{t('fields.campusName')}</Label>
              <Input
                id="campus-name"
                {...form.register('campusName')}
                placeholder={t('fields.campusNamePlaceholder')}
              />
              <Tip tipId="settings.campusName" />
              <FormFieldError message={fieldError('campusName')} />
            </div>
          </div>
        ) : null}

        {step === 1 ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="church-email">{t('fields.contactEmail')}</Label>
              <Input
                id="church-email"
                type="email"
                {...form.register('contactEmail')}
                placeholder={t('fields.contactEmailPlaceholder')}
                autoFocus
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>{t('fields.contactPhone')}</Label>
              <Controller
                control={form.control}
                name="contactPhone"
                render={({ field }) => (
                  <PhoneInput value={field.value ?? null} onChange={field.onChange} />
                )}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>{t('fields.contactAlternatePhone')}</Label>
              <Controller
                control={form.control}
                name="contactAlternatePhone"
                render={({ field }) => (
                  <PhoneInput value={field.value ?? null} onChange={field.onChange} />
                )}
              />
            </div>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label>{t('fields.country')}</Label>
              <Controller
                control={form.control}
                name="country"
                render={({ field }) => (
                  <CountrySelect
                    value={(field.value as CountryCode | null) || null}
                    onChange={(value) => field.onChange(value ?? '')}
                    priority={['GH']}
                  />
                )}
              />
              <FormFieldError message={fieldError('country')} />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>{t('fields.timezone')}</Label>
              <Controller
                control={form.control}
                name="timezone"
                render={({ field }) => (
                  <TimezoneSelect
                    value={field.value || null}
                    onChange={(value) => field.onChange(value ?? '')}
                  />
                )}
              />
              <FormFieldError message={fieldError('timezone')} />
            </div>

            <div className="mt-2 flex flex-col gap-1.5">
              <Label>{t('fields.location')}</Label>
              <LocationPicker
                value={mapLocation}
                onChange={(location) => {
                  form.setValue('locationLatitude', location?.latitude ?? null, {
                    shouldDirty: true,
                  });
                  form.setValue('locationLongitude', location?.longitude ?? null, {
                    shouldDirty: true,
                  });
                  form.setValue('address', location?.address ?? null, {
                    shouldDirty: true,
                  });
                }}
                country={(country as CountryCode | null) || null}
                mapClassName="h-72 min-h-72"
              />
              <FormFieldError message={fieldError('locationLatitude')} />
            </div>
          </div>
        ) : null}

        {step === 3 ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label>{t('fields.currency')}</Label>
              <Controller
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <CurrencySelect
                    value={field.value || null}
                    onChange={(value) => field.onChange(value ?? '')}
                    currencies={['GHS', 'USD', 'EUR', 'GBP', 'NGN', 'KES', 'ZAR']}
                  />
                )}
              />
              <FormFieldError message={fieldError('currency')} />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>{t('fields.locale')}</Label>
              <Controller
                control={form.control}
                name="locale"
                render={({ field }) => (
                  <Listbox
                    value={field.value || null}
                    onChange={(value) => field.onChange(value ?? '')}
                    options={LOCALES}
                  />
                )}
              />
              <FormFieldError message={fieldError('locale')} />
            </div>
          </div>
        ) : null}

        <FormFieldError message={submitError ?? undefined} />

        <div className="flex justify-end gap-2 pt-2">
          {step > 0 ? (
            <Button type="button" variant="outline" onClick={goBack}>
              {tCommon('action.back')}
            </Button>
          ) : null}
          {step < LAST_STEP ? (
            <Button type="submit">{tCommon('action.continue')}</Button>
          ) : (
            <Button type="submit" loading={create.isPending}>
              {t('create.submit')}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};
